import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import './Salon.css';

const todayString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const HolidaysTab = ({ salonId }) => {
  const { t, formatWeddingDate } = useLanguage();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newDate, setNewDate] = useState(todayString());
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [editName, setEditName] = useState('');

  const fetchHolidays = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/holidays`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { year }
      });
      setHolidays(response.data.holidays);
      setError('');
    } catch (err) {
      console.error('Error fetching holidays:', err);
      setError(t('timeOff.holidays.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [salonId, year, t]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const handlePrevYear = () => setYear(prev => prev - 1);
  const handleNextYear = () => setYear(prev => prev + 1);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (saving || !newDate || !newName.trim()) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/salons/${salonId}/holidays`, { date: newDate, name: newName.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewDate(todayString());
      setNewName('');
      fetchHolidays();
    } catch (err) {
      console.error('Error adding holiday:', err);
      alert(err.response?.data?.error || t('timeOff.holidays.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = (holiday) => {
    setEditingId(holiday.id);
    setEditDate(holiday.holiday_date);
    setEditName(holiday.name);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditDate('');
    setEditName('');
  };

  const handleSaveEdit = async (holidayId) => {
    if (!editDate || !editName.trim()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/salons/${salonId}/holidays/${holidayId}`, { date: editDate, name: editName.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingId(null);
      fetchHolidays();
    } catch (err) {
      console.error('Error updating holiday:', err);
      alert(err.response?.data?.error || t('timeOff.holidays.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (holidayId) => {
    if (!window.confirm(t('messages.confirm.deleteHoliday'))) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/salons/${salonId}/holidays/${holidayId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchHolidays();
    } catch (err) {
      console.error('Error deleting holiday:', err);
      alert(err.response?.data?.error || t('timeOff.holidays.failedToDelete'));
    }
  };

  return (
    <div>
      <div className="time-off-month-nav">
        <div className="time-off-month-controls">
          <button className="pagination-btn" onClick={handlePrevYear}>‹</button>
          <span className="time-off-month-label">{year}</span>
          <button className="pagination-btn" onClick={handleNextYear}>›</button>
        </div>
      </div>

      <div className="table-header">
        <h4>{t('timeOff.tabs.holidays')}</h4>
      </div>

      <form className="holiday-add-form" onSubmit={handleAdd}>
        <input
          type="date"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          required
        />
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={t('timeOff.holidays.namePlaceholder')}
          maxLength={255}
          required
        />
        <button type="submit" className="salon-button primary" disabled={saving}>
          {t('timeOff.holidays.addHoliday')}
        </button>
      </form>

      {loading ? (
        <div className="loading">{t('common.loading')}...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : holidays.length === 0 ? (
        <div className="table-empty-state">
          <div className="table-empty-state-icon">📅</div>
          <h4>{t('timeOff.holidays.noHolidays')}</h4>
        </div>
      ) : (
        <div className="table-container">
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('timeOff.hours.date')}</th>
                  <th>{t('timeOff.holidays.name')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {holidays.map((holiday) => (
                  <tr key={holiday.id}>
                    {editingId === holiday.id ? (
                      <>
                        <td>
                          <input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
                        </td>
                        <td>
                          <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} maxLength={255} />
                        </td>
                        <td>
                          <div className="table-actions">
                            <button className="action-button edit" onClick={() => handleSaveEdit(holiday.id)} disabled={saving} title={t('common.save')}>✓</button>
                            <button className="action-button delete" onClick={handleCancelEdit} disabled={saving} title={t('common.cancel')}>✕</button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{formatWeddingDate(holiday.holiday_date)}</td>
                        <td>{holiday.name}</td>
                        <td>
                          <div className="table-actions">
                            <button className="action-button edit" onClick={() => handleEditClick(holiday)} title={t('common.edit')}>✏️</button>
                            <button className="action-button delete" onClick={() => handleDelete(holiday.id)} title={t('common.delete')}>🗑️</button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default HolidaysTab;
